import { useState, useEffect, useCallback } from 'react'
import { useForm } from 'react-hook-form'
import { useDispatch } from 'react-redux'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { Auth } from 'aws-amplify'
import { useTranslation } from 'react-i18next'

import { Alert } from '@/actions'
import { useAnalytics } from '@/contexts/AnalyticsProvider'
import Button from '@/components/Button'
import { TextField } from '@/components/TextField'
// Import optimized React SVG component generated by SVGR
import DrkTrimMainLogo from '../../components/svg/DrkTrimMain'

// scss files
import scss from './Login.scss'

const isExpiredTemporaryPasswordError = error =>
  error?.code === 'NotAuthorizedException' &&
  /temporary password.*expired|expired.*temporary password/i.test(
    error?.message ?? ''
  )

export default function Login(props) {
  const { t } = useTranslation()
  const [isLoading, setIsLoading] = useState(false)
  const [passwordChallenge, setPasswordChallenge] = useState(null)
  const [newPassword, setNewPassword] = useState('')
  const [confirmNewPassword, setConfirmNewPassword] = useState('')
  const { trackEvent } = useAnalytics()
  const dispatch = useDispatch()
  const location = useLocation()
  const navigate = useNavigate()
  const emailParam =
    new URLSearchParams(location.search).get('email')?.trim().toLowerCase() ??
    ''

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    mode: 'onSubmit',
    defaultValues: {
      email: emailParam,
      password: '',
    },
  })

  useEffect(() => {
    document.getElementsByTagName('body')[0].className = scss.boxedFormPage
    return () => {
      document.getElementsByTagName('body')[0].className = ''
    }
  }, [])

  const handleError = useCallback(
    (error, email) => {
      trackEvent('auth_login_failed', {
        error_code: error.code,
        error_message: error.message,
      })

      switch (error.code) {
        case 'UserNotConfirmedException':
          Auth.resendSignUp(email)
            .then(result => {
              if (result?.CodeDeliveryDetails) {
                dispatch(Alert({ error: t('login.errors.checkEmail') }))
              }
            })
            .catch(err => {
              console.error('ResendEmail', err)
              dispatch(
                Alert({
                  error: t('login.errors.verificationError', {
                    message: err.message,
                  }),
                })
              )
            })
          break
        case 'UserNotFoundException':
          error.message = t('login.errors.incorrectCredentials')
        // falls through
        case 'PasswordResetRequiredException':
          dispatch(Alert({ error: error.message }))
          break
        case 'NotAuthorizedException':
          dispatch(
            Alert({
              error: isExpiredTemporaryPasswordError(error)
                ? t('login.errors.expiredTemporaryPassword')
                : error.message,
            })
          )
          break
        default:
          dispatch(Alert({ error: error.message }))
          console.error(error)
          break
      }
    },
    [dispatch, t, trackEvent]
  )

  const handleLoginSuccess = useCallback(
    (loginInfo, password) => {
      const userPayload = loginInfo.signInUserSession.idToken.payload
      const accessToken = loginInfo.signInUserSession.accessToken.jwtToken

      setIsLoading(false)
      setPasswordChallenge(null)
      setNewPassword('')
      setConfirmNewPassword('')
      trackEvent('auth_login_completed', {})
      if (location.pathname === '/login' || location.pathname === '/') {
        navigate('/')
      }
      props.handleLoginAttempt(true, userPayload, { accessToken, password })
    },
    [location.pathname, navigate, props, trackEvent]
  )

  const onSubmit = useCallback(
    data => {
      const email = (data.email ?? '').trim().toLowerCase()
      setIsLoading(true)
      setPasswordChallenge(null)

      trackEvent('auth_login_attempted', {})

      Auth.signIn(email, data.password)
        .then(loginInfo => {
          if (loginInfo.signInUserSession != null) {
            handleLoginSuccess(loginInfo, data.password)
          } else if (loginInfo.challengeName === 'NEW_PASSWORD_REQUIRED') {
            trackEvent('auth_password_reset_required', {})
            setPasswordChallenge({ email, user: loginInfo })
            setIsLoading(false)
          } else if (loginInfo.challengeName === 'INVALIDEMAIL') {
            trackEvent('auth_invalid_email_challenged', {})

            Auth.resendSignUp(email)
              .catch(err => {
                handleError(err, email)
              })
              .finally(() => {
                setIsLoading(false)
              })
          } else {
            dispatch(Alert({ error: t('login.errors.unsupportedChallenge') }))
            setIsLoading(false)
          }
        })
        .catch(err => {
          handleError(err, email)
          setIsLoading(false)
        })
    },
    [dispatch, handleError, handleLoginSuccess, t, trackEvent]
  )

  const handleCompleteNewPassword = useCallback(
    event => {
      event.preventDefault()

      if (!passwordChallenge) return
      if (!newPassword) {
        dispatch(Alert({ error: t('login.newPassword.errors.required') }))
        return
      }
      if (newPassword !== confirmNewPassword) {
        dispatch(Alert({ error: t('login.newPassword.errors.mismatch') }))
        return
      }

      setIsLoading(true)
      Auth.completeNewPassword(passwordChallenge.user, newPassword, {})
        .then(loginInfo => {
          if (loginInfo.signInUserSession != null) {
            handleLoginSuccess(loginInfo, newPassword)
            return null
          }

          return Auth.signIn(passwordChallenge.email, newPassword).then(
            signedInInfo => handleLoginSuccess(signedInInfo, newPassword)
          )
        })
        .catch(err => {
          handleError(err, passwordChallenge.email)
          setIsLoading(false)
        })
    },
    [
      confirmNewPassword,
      dispatch,
      handleError,
      handleLoginSuccess,
      newPassword,
      passwordChallenge,
      t,
    ]
  )

  if (passwordChallenge) {
    return (
      <form
        onSubmit={handleCompleteNewPassword}
        className={scss['login-container']}
      >
        <div className={scss['login-header']}>
          <DrkTrimMainLogo
            className={scss['login-logo']}
            role='img'
            aria-label='MyAssetMap Logo'
          />
          <h1 className={scss['login-title']}>
            {t('login.newPassword.title')}
          </h1>
          <p className={scss['login-subtitle']}>
            {t('login.newPassword.subtitle')}
          </p>
        </div>

        <div className={scss['login-form']}>
          <TextField
            label={t('login.email')}
            type='text'
            autoComplete='email'
            inverse
            disabled
            id='login-challenge-email'
            value={passwordChallenge.email}
          />

          <TextField
            label={t('login.newPassword.password')}
            type='password'
            autoComplete='new-password'
            inverse
            required
            id='login-new-password'
            value={newPassword}
            onChange={event => setNewPassword(event.target.value)}
          />

          <TextField
            label={t('login.newPassword.confirmPassword')}
            type='password'
            autoComplete='new-password'
            inverse
            required
            id='login-confirm-new-password'
            value={confirmNewPassword}
            onChange={event => setConfirmNewPassword(event.target.value)}
          />

          <Button
            type='submit'
            data-testid='login-new-password-button'
            disabled={
              isLoading || !newPassword || newPassword !== confirmNewPassword
            }
            loading={isLoading}
            label={
              isLoading
                ? t('login.newPassword.saving')
                : t('login.newPassword.submit')
            }
            fullWidth
          />
        </div>

        <div className={scss['login-footer']}>
          <button
            type='button'
            className={scss['login-link-button-text']}
            onClick={() => {
              setPasswordChallenge(null)
              setNewPassword('')
              setConfirmNewPassword('')
            }}
          >
            {t('login.newPassword.backToLogin')}
          </button>
        </div>
      </form>
    )
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className={scss['login-container']}>
      <div className={scss['login-header']}>
        <DrkTrimMainLogo
          className={scss['login-logo']}
          role='img'
          aria-label='MyAssetMap Logo'
        />
        <h1 className={scss['login-title']}>{t('login.welcomeBack')}</h1>
        <p className={scss['login-subtitle']}>{t('login.signInToContinue')}</p>
      </div>

      <div className={scss['login-form']}>
        <TextField
          label={t('login.email')}
          type='text'
          autoComplete='email'
          inverse
          required
          error={errors.email?.message}
          id='login-email'
          data-testid='login-email'
          {...register('email', {
            required: t('login.errors.fieldEmailRequired'),
          })}
        />

        <TextField
          label={t('login.password')}
          type='password'
          autoComplete='current-password'
          inverse
          required
          error={errors.password?.message}
          id='login-password'
          data-testid='login-password'
          {...register('password', {
            required: t('login.errors.fieldPasswordRequired'),
          })}
        />

        <div className={scss['login-options']}>
          <label htmlFor='login-remember-me' className={scss['checkbox-label']}>
            <input
              id='login-remember-me'
              type='checkbox'
              defaultChecked
              className={scss['checkbox-input']}
            />
            <span className={scss['checkbox-custom']} />
            <span className={scss['checkbox-text']}>
              {t('login.rememberMe')}
            </span>
          </label>
          <Link className={scss['forgot-link']} to='/reset'>
            {t('login.forgotPassword')}
          </Link>
        </div>

        <Button
          type='submit'
          data-testid='login-button'
          disabled={isLoading}
          loading={isLoading}
          label={isLoading ? t('login.loggingIn') : t('login.loginButton')}
          fullWidth
        />
      </div>
    </form>
  )
}
